import api from '@/libs/axiosInstance';
import type {
  ApiResponse,
  BrandMatchScoresRequest,
  BrandMatchScoresResponse,
} from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Map keyed by the identifier the card renders with. Creator-api results
 * use internal creator UUIDs; legacy Meta results use platform user ids.
 */
type ScoreMap = Record<string, number>;

const fetchBatchScores = async (
  platformUserIds: string[],
  creatorIds: string[] = []
): Promise<ScoreMap> => {
  if (platformUserIds.length === 0 && creatorIds.length === 0) return {};

  const body: BrandMatchScoresRequest =
    creatorIds.length > 0 ? { socialProfileIds: creatorIds } : { platformUserIds };
  const { data } = await api.post<ApiResponse<BrandMatchScoresResponse>>(
    '/dashboard/brand-match/scores',
    body
  );

  if (!data.data) throw new Error('Empty brand-match scores response');
  const includeCreatorIdKeys = creatorIds.length > 0;
  return data.data.scores.reduce<ScoreMap>((acc, entry) => {
    acc[entry.platformUserId] = entry.score;
    if (includeCreatorIdKeys) acc[entry.socialProfileId] = entry.score;
    return acc;
  }, {});
};

/**
 * Fetch brand-match scores for a batch of creators identified by their
 * platform user ids (the only id the Discovery results carry). The result
 * is a `Record<platformUserId, score>` so consumers can look up by the
 * same key they render with.
 *
 * The query is enabled only when a brand context exists and the caller has
 * at least one platform user id; an empty list short-circuits without a
 * network call.
 */
export const useBatchMatchScoresQuery = (
  brandId: string,
  platformUserIds: string[],
  enabled: boolean = true,
  creatorIds: string[] = []
) => {
  // Sort to keep the query key stable regardless of caller render order.
  const sortedIds = useMemo(() => [...platformUserIds].sort(), [platformUserIds]);
  const sortedCreatorIds = useMemo(() => [...creatorIds].sort(), [creatorIds]);

  return useQuery<ScoreMap, Error>({
    queryKey: ['match-scores', brandId, sortedIds, sortedCreatorIds],
    queryFn: () => fetchBatchScores(sortedIds, sortedCreatorIds),
    enabled: enabled && !!brandId && (sortedIds.length > 0 || sortedCreatorIds.length > 0),
    staleTime: ONE_DAY_MS,
  });
};
